import {
  AclEntryType,
  Address,
  Coordinates,
  DomainReference,
  Location,
  LocationGroup,
} from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

export class EmbeddableLocation {
  locationId?: number;
  locationName?: string;
  code?: string;
  addressLine1?: string;
  addressLine2?: string;
  addressLine3?: string;
  city?: string;
  state?: string;
  postalCode?: string;
  country?: string = 'USA';
  coordinates?: Coordinates;
  timeZone?: string;
  locationGroupIds: string[] = [];
  hasBeenValidated = false;

  /**
   * The owner of the saved location if this is a ficon location
   *
   * _Note:_ This is a frontend-only field.
   */
  owner?: DomainReference;

  constructor(opts?: Partial<EmbeddableLocation>) {
    if (opts?.locationId != null) {
      this.locationId = opts.locationId;
    }
    if (opts?.locationName != null) {
      this.locationName = opts.locationName;
    }
    if (opts?.code != null) {
      this.code = opts.code;
    }
    if (opts?.addressLine1 != null) {
      this.addressLine1 = opts.addressLine1;
    }
    if (opts?.addressLine2 != null) {
      this.addressLine2 = opts.addressLine2;
    }
    if (opts?.addressLine3 != null) {
      this.addressLine3 = opts.addressLine3;
    }
    if (opts?.city != null) {
      this.city = opts.city;
    }
    if (opts?.state != null) {
      this.state = opts.state;
    }
    if (opts?.postalCode != null) {
      this.postalCode = opts.postalCode;
    }
    if (opts?.country != null) {
      this.country = opts.country;
    }
    if (opts?.timeZone != null) {
      this.timeZone = opts.timeZone;
    }
    if (opts?.locationGroupIds != null) {
      this.locationGroupIds = [...opts.locationGroupIds];
    }
    if (opts?.hasBeenValidated != null) {
      this.hasBeenValidated = opts.hasBeenValidated;
    }
    if (opts?.owner != null) {
      this.owner = opts.owner;
    }
  }

  get requiresValidation(): boolean {
    return !this.locationId && !this.hasBeenValidated;
  }

  get cityState() {
    const arr = [this.city, this.state].filter((s) => !!s);
    return arr.length ? arr.join(', ') : undefined;
  }

  get cityStatePostalCode() {
    const arr = [this.cityState, this.postalCode].filter((s) => !!s);
    return arr.length ? arr.join(' ') : undefined;
  }

  get addressString() {
    const arr = [this.addressLine1, this.cityStatePostalCode].filter(
      (s) => !!s,
    );
    return arr.length ? arr.join(' ') : undefined;
  }

  toAddress(): Address {
    return new Address({
      addressLine1: this.addressLine1,
      addressLine2: this.addressLine2,
      addressLine3: this.addressLine3,
      locality: this.city,
      region: this.state,
      postalCode: this.postalCode,
      country: this.country,
    });
  }

  toLocation(): Location {
    const address = this.toAddress();
    return new Location({
      id: this.locationId,
      name: this.locationName,
      code: this.code,
      ownerId: this.owner?.id,
      address,
      timeZone: this.timeZone,
      coordinates: this.coordinates,
      groups: this.locationGroupIds.map(
        (groupId) => new LocationGroup({ naturalId: groupId }),
      ),
    });
  }

  hasSameAddress(other?: EmbeddableLocation): boolean {
    if (!other) {
      return false;
    }

    return (
      this.addressLine1 === other.addressLine1 &&
      this.city === other.city &&
      this.state === other.state &&
      this.postalCode === other.postalCode &&
      this.country === other.country
    );
  }

  static fromLocation(location: Location): EmbeddableLocation {
    const owningAclEntry = location.acl?.find(
      (aclEntry) => aclEntry.type === AclEntryType.OWNER,
    );

    return new EmbeddableLocation({
      locationId: location.id,
      locationName: location.name,
      code: location.code,
      addressLine1: location.address?.addressLine1,
      addressLine2: location.address?.addressLine2,
      addressLine3: location.address?.addressLine3,
      city: location.address?.locality,
      state: location.address?.region,
      postalCode: location.address?.postalCode,
      country: location.address?.country,
      timeZone: location.timeZone,
      locationGroupIds: location.groups
        .map((group) => group.naturalId)
        .filter(notNull),
      hasBeenValidated: true,
      owner: owningAclEntry?.domain,
    });
  }

  // TODO: Replace the second two arguments with a partial `EmbeddableLocation`
  static fromAddress(
    address?: Address,
    locationName?: string,
    timeZone?: string,
  ): EmbeddableLocation {
    return new EmbeddableLocation({
      locationName,
      addressLine1: address?.addressLine1,
      addressLine2: address?.addressLine2,
      addressLine3: address?.addressLine3,
      city: address?.locality,
      state: address?.region,
      postalCode: address?.postalCode,
      country: address?.country,
      timeZone,
    });
  }
}
